import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../../components/Loading';
import ReactModal from '../../../../components/Modal';
import ReactSelect from '../../../../components/ReactSelect';
import { COUNTRIES } from '../../create/countries';
import { LANGUAGES } from '../../create/languages';
import ErrorMessage from '../../ErrorMessage';
import PromptsView from '../../promptGenerator/components/PromptsView';
import AddKeywordModal from '../../promptGenerator/components/addKeywordModal';
import { generatePrompts, getPromptGenerationApi } from '../query';
import {
    resolveCountryLanguage,
    saveCountryLanguage,
} from '../../../../helpers/aiTrackingCountryLanguage';

const POLL_INTERVAL = 5000;

/**
 * Generates prompts for one topic that has none yet, and lets the user track the result.
 *
 * The prompt generator asks for a market before it generates anything; here the trigger is a
 * single click on a row, so the market is inferred instead — from the country/language the user
 * last generated with, falling back to the ones this project's prompts are already tracked with.
 * When that inference works the run starts as the modal opens; when it does not (a project with
 * no prompts, on a browser that has never used the generator) the two selects are shown and the
 * user starts the run themselves. The same selects come back after a failure so a retry can
 * correct the market.
 *
 * Generation is asynchronous server-side: a `pending` response is polled until it completes.
 */
class GeneratePromptsModal extends Component {
    constructor(props) {
        super(props);

        this.startGeneration = this.startGeneration.bind(this);
        this.handleSelectCountry = this.handleSelectCountry.bind(this);
        this.handleSelectLanguage = this.handleSelectLanguage.bind(this);
        this.showOpened = this.showOpened.bind(this);
        this.onSelectPrompts = this.onSelectPrompts.bind(this);
        this.onSelectAllPrompts = this.onSelectAllPrompts.bind(this);
        this.openAddKeywordModal = this.openAddKeywordModal.bind(this);
        this.closeAddKeywordModal = this.closeAddKeywordModal.bind(this);

        const { country, language } = this.getDefaultCountryLanguage();

        this.canStartOnOpen = !!(
            country &&
            language &&
            props.project?.own_domain &&
            props.project?.own_product_name
        );

        this.state = {
            country,
            language,
            // Set up front so the selects never flash before componentDidMount starts the run.
            generating: this.canStartOnOpen,
            generatedTopic: null,
            selectedPrompts: [],
            isAddKeywordModalOpen: false,
            errorMessage: '',
            errors: {},
        };
    }

    componentDidMount() {
        if (this.canStartOnOpen) {
            this.startGeneration();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        if (this.pollTimeout) clearTimeout(this.pollTimeout);
    }

    /**
     * Prefer the project's own defaults, then the country/language the user picked last in the
     * prompt generator, and finally the ones the project's prompts are already tracked with, so
     * generation works without asking.
     */
    getDefaultCountryLanguage() {
        const firstPrompt = this.props.project?.prompts?.[0] ?? null;

        return resolveCountryLanguage(this.props.project, {
            country: firstPrompt?.country,
            language: firstPrompt?.language,
        });
    }

    rememberCountryLanguage() {
        saveCountryLanguage({
            country: this.state.country,
            language: this.state.language,
        });
    }

    handleSelectCountry(data) {
        this.setState((prevState) => ({
            country: data.value,
            // COUNTRIES carries each country's primary language, but a few entries have none;
            // those must not wipe a language the user already picked.
            language: data.language || prevState.language,
            errors: { ...prevState.errors, country: null, language: null },
        }));
    }

    handleSelectLanguage(value) {
        this.setState((prevState) => ({
            language: value,
            errors: { ...prevState.errors, language: null },
        }));
    }

    async startGeneration() {
        const { t, project, topic } = this.props;
        const errors = {};

        if (!project?.own_domain || !project?.own_product_name) {
            errors.project = t('ai_tracking.prompt_generator.brand_and_domain_required');
        }
        if (!this.state.country) {
            errors.country = t('ai_tracking.prompt_generator.country_required');
        }
        if (!this.state.language) {
            errors.language = t('ai_tracking.prompt_generator.language_required');
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ generating: false, errors });
            return;
        }

        this.setState({
            generating: true,
            errors: {},
            errorMessage: '',
            generatedTopic: null,
            selectedPrompts: [],
        });
        this.rememberCountryLanguage();

        try {
            const response = await generatePrompts({
                brand_name: project.own_product_name,
                brand_domain: project.own_domain,
                country: this.state.country,
                language: this.state.language,
                project: project.id,
                topic_ids: [topic.id],
            });
            const data = await response.json();

            if (data?.status === 'completed') {
                this.onGenerated(data?.topics);
            } else if (data?.status === 'pending') {
                this.pollGeneration(data.id);
            } else {
                this.onFailed(data?.error_message ?? data?.detail);
            }
        } catch (err) {
            console.log('err', err);
            this.onFailed();
        }
    }

    async pollGeneration(id) {
        try {
            const response = await getPromptGenerationApi(id);
            const data = await response.json();
            if (this.unmounted) return;

            if (data?.status === 'completed') {
                this.onGenerated(data?.topics);
            } else if (data?.status === 'pending') {
                this.pollTimeout = setTimeout(() => this.pollGeneration(id), POLL_INTERVAL);
            } else {
                this.onFailed(data?.error_message ?? data?.detail);
            }
        } catch (err) {
            console.log('err', err);
            this.onFailed();
        }
    }

    onGenerated(topics) {
        if (this.unmounted) return;
        // Only ever one topic is requested, so the response carries only that one back.
        this.setState({ generating: false, generatedTopic: topics?.[0] ?? null });
        this.props.onGenerated?.();
    }

    onFailed(message) {
        if (this.unmounted) return;
        this.setState({
            generating: false,
            errorMessage:
                message ?? this.props.t('ai_tracking.prompt_generator.failed_to_generate_prompts'),
        });
    }

    showOpened() {
        return 'show';
    }

    onSelectPrompts(prompt) {
        this.setState((prevState) => ({
            selectedPrompts: prevState.selectedPrompts.includes(prompt)
                ? prevState.selectedPrompts.filter((item) => item !== prompt)
                : [...prevState.selectedPrompts, prompt],
        }));
    }

    onSelectAllPrompts(prompts) {
        this.setState((prevState) => {
            const allSelected = prompts.every((prompt) =>
                prevState.selectedPrompts.includes(prompt),
            );
            return {
                selectedPrompts: allSelected
                    ? prevState.selectedPrompts.filter((item) => !prompts.includes(item))
                    : [...new Set([...prevState.selectedPrompts, ...prompts])],
            };
        });
    }

    openAddKeywordModal() {
        this.setState({ isAddKeywordModalOpen: true });
    }

    closeAddKeywordModal() {
        this.setState({ isAddKeywordModalOpen: false });
    }

    render() {
        const { t, topic, project, isOpen, onClose } = this.props;
        const { generating, generatedTopic, errorMessage, errors } = this.state;
        const alreadyTrackedPrompts = project?.prompts?.map((item) => item.prompt) ?? [];

        // The market is only asked for when it could not be inferred, or when a failed run needs
        // correcting — a successful run has already answered the question.
        const showMarketForm = !generating && !generatedTopic;

        return (
            <>
                <ReactModal
                    modalState={isOpen}
                    className="width-sm ai-tracking-modal generate-prompts-modal"
                    onClose={onClose}
                >
                    <h3>{t('ai_tracking.prompt_generator.generate_prompts')}</h3>
                    <div className="ai-tracking-modal-body">
                        <div className="generate-prompts-modal-topic">
                            <div className="generate-prompts-modal-topic-name">{topic?.name}</div>
                            {!!topic?.description && (
                                <div className="generate-prompts-modal-topic-description">
                                    {topic.description}
                                </div>
                            )}
                        </div>

                        {showMarketForm && (
                            <>
                                <div className="generate-prompts-modal-market">
                                    <div>
                                        <ReactSelect
                                            options={COUNTRIES}
                                            value={
                                                COUNTRIES.find(
                                                    (item) => item.value === this.state.country,
                                                ) ?? null
                                            }
                                            label={
                                                <div className="select-label">
                                                    {t('ai_tracking.add_keyword.country')}{' '}
                                                    <span>*</span>
                                                </div>
                                            }
                                            placeholder={t('ai_tracking.add_keyword.country')}
                                            customeOnChange={this.handleSelectCountry}
                                            blurInputOnSelect
                                            classname="mr-0"
                                            isSearchable
                                            components={{ IndicatorSeparator: () => null }}
                                        />
                                        {errors.country && (
                                            <div className="error-message">{errors.country}</div>
                                        )}
                                    </div>
                                    <div>
                                        <ReactSelect
                                            options={LANGUAGES}
                                            value={
                                                LANGUAGES.find(
                                                    (item) => item.value === this.state.language,
                                                ) ?? null
                                            }
                                            label={
                                                <div className="select-label">
                                                    {t('ai_tracking.add_keyword.language')}{' '}
                                                    <span>*</span>
                                                </div>
                                            }
                                            placeholder={t('ai_tracking.add_keyword.language')}
                                            onChange={this.handleSelectLanguage}
                                            blurInputOnSelect
                                            classname="mr-0"
                                            isSearchable
                                            components={{ IndicatorSeparator: () => null }}
                                        />
                                        {errors.language && (
                                            <div className="error-message">{errors.language}</div>
                                        )}
                                    </div>
                                </div>
                                {errors.project && (
                                    <div className="error-message">{errors.project}</div>
                                )}
                            </>
                        )}

                        {generating && (
                            <div className="mt-20 d-flex gap-8 align-items-center">
                                <strong>
                                    {t('ai_tracking.prompt_generator.generate_message')}
                                </strong>
                                <Loading />
                            </div>
                        )}

                        {!!errorMessage && <ErrorMessage message={errorMessage} />}

                        {!!generatedTopic && (
                            <div className="mt-20">
                                <PromptsView
                                    item={generatedTopic}
                                    headerKey="name"
                                    promptKey="generated_prompts"
                                    uniqueKey="l-0"
                                    showOpened={this.showOpened}
                                    onClickOnStep={() => {}}
                                    selectedItems={this.state.selectedPrompts}
                                    onSelectPrompts={this.onSelectPrompts}
                                    onSelectAllPrompts={this.onSelectAllPrompts}
                                    disabledPrompts={alreadyTrackedPrompts}
                                    hideCollapse
                                />
                            </div>
                        )}
                    </div>
                    <div className="ai-tracking-modal-footer">
                        {generatedTopic ? (
                            <button
                                type="button"
                                className="button button-filled button-primary width-sm btn"
                                onClick={this.openAddKeywordModal}
                                disabled={this.state.selectedPrompts.length === 0}
                            >
                                {t('ai_tracking.prompt_generator.track_selected_prompts')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                                onClick={this.startGeneration}
                                disabled={generating}
                            >
                                {generating && <Loading onlyPreloader />}
                                {t('ai_tracking.prompt_generator.generate_prompts')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="button button-outline button-primary width-sm btn"
                            onClick={onClose}
                        >
                            {t('ai_tracking.projects.edit.close')}
                        </button>
                    </div>
                </ReactModal>
                {this.state.isAddKeywordModalOpen && (
                    <AddKeywordModal
                        isOpen={this.state.isAddKeywordModalOpen}
                        onClose={this.closeAddKeywordModal}
                        prompts={this.state.selectedPrompts}
                        projectId={project?.id}
                    />
                )}
            </>
        );
    }
}

export default withTranslation()(GeneratePromptsModal);
